package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 顶层 component 节点（与 ScreenAst 平级）。
 *
 * @property id 组件名，由文件资源路径推导
 * @property props 组件 prop 声明列表
 * @property root 组件根元素（编译期展开模板）
 * @property animations 组件内部声明的 animation 列表，在展开时合并到 screen.animations
 * @property state 组件级 `state defaults { ... }` 声明；未声明时为 null。
 *   每个组件实例在展开期被「提升 + 实例 id 改名」为 `instanceId/field`，并入 screen.state，
 *   从而让每个实例拥有互不串扰的独立 state 作用域（运行时仍是扁平 state 信号表，零改动）。
 */
data class ComponentAst(
    val id: String,
    val props: List<PropDecl>,
    val root: ElementAst,
    val animations: List<AnimationAst> = emptyList(),
    val state: StateAst? = null,
    /** `import "path" as Alias` 顶层声明列表（ADR-0038）。 */
    val imports: List<ImportDecl> = emptyList(),
    /** DSL 文件头；未声明时按 legacy 处理。 */
    val dslHeader: DslHeaderAst? = null,
    /** v2 component 内的局部 model 声明。 */
    val model: ModelAst? = null,
    override val position: SourcePosition,
) : AstNode {

    /**
     * 单个 prop 声明。defaultValue == null 表示必传。
     *
     * 当 [type] == [PropType.DATA] 时，[dataTypeName] 必须非空，指向 screen 顶层 data 声明的名字
     * （如 "Item"）；其它 5 类 prop 时 [dataTypeName] 必为 null。
     */
    data class PropDecl(
        val name: String,
        val type: PropType,
        val defaultValue: ExprAst?,
        val position: SourcePosition,
        /** M6 review B：data 类型 prop 的目标 data 名；非 DATA 类型时必为 null。 */
        val dataTypeName: String? = null,
    )

    /**
     * 第一版 prop 类型集合，无 nullable / 无泛型。
     *
     * - STRING/NUMBER/BOOL/COLOR/FUN：5 类基础内置类型
     * - DATA：M6 review B 引入，指向顶层 `data "Name" { ... }` 声明的复合类型；
     *   实际目标 data 名记录在 [PropDecl.dataTypeName]
     */
    enum class PropType { STRING, NUMBER, BOOL, COLOR, FUN, DATA }
}
